import React, { useState, MouseEvent, useEffect } from 'react';
import classNames from 'classnames';
import { useForm } from 'react-hook-form';
import FormInput from '../../common/FormInput/FormInput'; 
import { DefaultConfig } from '../../default-config';
import Constant from '../../static/data/Constant';
import { generateId } from '../../common/util/Functions';
import SendPassordPaSMS from '../SendPassordPaSMS/SendPassordPaSMS';
import { InputContent, ErrorItem, StringObject, Translations } from '../../types';
import { useSiteLang, useTranslation } from '../../common/util/Hook';
import { StartActivationResult } from '../../generated-types';
/* eslint-disable */
// @ts-ignore
import BankIDavtaleTermNoPDF from '../../static/data/Bankid-Avtale-Eika-v.2026.05.01.pdf';
// @ts-ignore
import BankIDavtaleTermEngPDF from '../../static/data/Bankid-Agreement-Eika-v.2026.05.01.pdf';
/* eslint-enable */

import './Aktivering.scss';
import { BankidAvtaleTermEn, BankidAvtaleTermNo } from '../../static/data/Bankid-Avtale-Eika-v.2026.05.01';

export interface AktiveringProps {
    config: typeof DefaultConfig;
    useH2?: boolean;
    // eslint-disable-next-line
    submit?: (data: StringObject) => void;
    startOver?:() => void;
    standAlone?: boolean;
    failed?: string;
    activation?: StartActivationResult;
}

interface TextTag {
    tag: string;
    text: string;
}

interface State {
    id: string;
    formAction: any;
    content: InputContent[];
}

const getTextTag = (text=''): TextTag => {
    let tag = 'div';
    const regH1 = /^#title1/;
    const regH2 = /^#title2/;
    const regH3 = /^#title3/;
    const regH4 = /^#title4/;
    const strong = /^#strong/;
    const point = /^#point/;
    const dash = /^(\s+)?-(\s+)/;

    if ( regH1.test(text) ){
        tag = 'h1';
        text = text.replace( regH1, '' );
    } else if ( regH2.test(text) ){
        tag = 'h2';
        text = text.replace( regH2, '' );
    } else if ( regH3.test(text) ){
        tag = 'h3';
        text = text.replace( regH3, '' );
    } else if ( regH4.test(text) ){
        tag = 'h4';
        text = text.replace( regH4, '' );
    } else if ( strong.test(text) ){
        tag = 'strong';
        text = text.replace( strong, '' );
    } else if ( point.test(text) || dash.test(text) ){
        tag = 'point';
        text = text.replace( point, '' );
    }

    return {tag, text: text.trim()};
};

const getInputContent = (translated: Translations, isNorwegian: boolean): InputContent[] => {
    const terms = isNorwegian ? BankidAvtaleTermNo : BankidAvtaleTermEn;
    return [
        {
            id: 'password',
            name: 'password',
            label: translated.passord,
            type: Constant.typePassword,
            required: true,
            namespace: 'password',
            fieldButton: {
                type: Constant.typeFieldButtonPasswordReveal,
                action: 'toggle-password-reveal',
                mode: 'hidden',
                textPin: {
                    'hidden': translated.visPassord,
                    'visible': translated.skjulPassord,
                }
            },
            validation: [
                {
                    rule: Constant.validationRequired,
                    message: translated.passordPakrevd,
                }, {
                    rule: Constant.validationPassword,
                    message: translated.passordError,
                }
            ]           
        }, { 
            id: 'agreement',
            name: 'agreement',
            type: Constant.typeElement,
            element: <div className="paragraph-small -only-top">
                <section aria-label={translated.bankIdAvtaleVilkar} className="agreement-info-wrapper">
                    { terms.split("\n").map( (text: string, i: number) => {
                        if ( i === 0 && !text ) { return null; }

                        const key = `term-text-${i}`;
                        const config = getTextTag(text);
                        let output = <p key={key}>{config.text}</p>;
                        if ( config.tag === 'h1' ) {
                            output = <h1 key={key} className="typography-h1">{config.text}</h1>;
                        } else if ( config.tag === 'h2' ) {
                            output = <h2 key={key} className="typography-h2">{config.text}</h2>;
                        } else if ( config.tag === 'h3' ) {
                            output = <h3 key={key} className="typography-h3">{config.text}</h3>;
                        } else if ( config.tag === 'h4' ) {
                            output = <h4 key={key} className="typography-h4">{config.text}</h4>;
                        } else if ( config.tag === 'strong' ) {
                            output = <div key={key} className="strong">{config.text}</div>;
                        } else if ( config.tag === 'point' ) {
                            output = <div key={key} className="point">{config.text}</div>;
                        } else if ( config.text === '' ) {
                            output = <div key={key} className="paragraph-small">{config.text}</div>;
                        }

                        return output;
                    })}
                </section>

                <div className="paragraph-small">
                    <a href={isNorwegian ? BankIDavtaleTermNoPDF : BankIDavtaleTermEngPDF} target="blank" download={isNorwegian ? "Bankid-Avtale-Eika-v.2026.05.01.pdf" : "Bankid-Agreement-Eika-v.2026.05.01.pdf"} rel="noreferrer" className="bank-id-link agreement-link">{translated.lastNedBankIdAvtaleVilkarPDF}</a>
                </div>
            </div>
        }, {
            id: 'terms',
            name: 'terms',
            legend: 'Bekreftelse',
            type: Constant.typeCheckbox,
            namespace: 'term',
            list: [
                {
                    label: translated.bankIdAvtaleGodtarVilkaar,
                    value: 'yes',
                }
            ],
            validation: [
                {
                    rule: Constant.validationRequired,
                    message: translated.acceptTermsMessage,
                }
            ]
        },
    ];
};


const click = (state: State, setState: (s: State) => void, e: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement>, isNorwegian: boolean, translated: Translations, key = ''): void => {
    if ( e && typeof(e.preventDefault) === 'function' ) {
        e.preventDefault();
    }

    if ( key === 'toggle-password-reveal' ) {
        const content = getInputContent(translated, isNorwegian);
        const password = content.find( (data: InputContent) => data.id === 'password');
        if ( !password || !password?.fieldButton ) { return; }

        const current = state.content.find( (data: InputContent) => data.id === 'password');
        if ( !current || !current?.fieldButton ){ return; }

        password.fieldButton.mode = current.fieldButton.mode === 'hidden' ? 'visible' : 'hidden';
        password.type = password.fieldButton.mode === 'hidden' ? Constant.typePassword : Constant.typeText;

        setState({...state, content});
    }
};

const submit = (props: AktiveringProps, data: StringObject, errorList: ErrorItem[]): void => {
    if ( errorList.length ) { return; }

    if ( props.submit ) { 
        props.submit(data); 
    }
};

const startOver = (props: AktiveringProps): void => {
    if(props.startOver) {
        props.startOver()
    }
}

const Aktivering: React.FC<AktiveringProps> = (props): React.ReactElement => {

    const { siteLang } = useSiteLang();

    const translated = useTranslation();
    const [state, setState] = useState<State>({
        id: generateId('aktivering-mobile'),
        formAction: useForm<any>({mode: 'onBlur'}),
        content: getInputContent(translated, siteLang === 'nb'),
    });

    useEffect(() => {
        setState(prevState => ({
            ...prevState,
            content: getInputContent(translated, siteLang === 'nb'),
        }));
    }, [siteLang, translated]);

    return (
        <div id={state.id} className={classNames({
            'stand-alone-wrapper': !!props.standAlone,
        })}>
            { props.useH2 ?
                <h2 className="paragraph-extra-small">{translated.bankIdAvtaleAktivering}</h2> :
                <h1 className="paragraph-extra-small">{translated.bankIdAvtaleAktivering}</h1>
            }

            <p>{translated.bankIdAvtalePassordBeskrivelse}</p>
            <p>{translated.bankidAvtalePassordRegistrertNummer} <b>{[props.activation?.phoneNumberHint || '*** ** ***']}</b></p>

            <SendPassordPaSMS {...props} namespace="paragraph-large" />

            <FormInput 
                content={state.content} 
                formAction={state.formAction} 
                submitText={translated.aktiver}
                submitCallback={ (data: StringObject, errorList: ErrorItem[], cancel?) => {
                    if(cancel) {
                        startOver(props)
                    } else {
                        submit(props, data, errorList );
                    }
                }}
                failed={props.failed}
                cancelText={translated.startPaaNytt}
                click={(e: MouseEvent<HTMLButtonElement | HTMLAnchorElement>, k='') => { click(state, setState, e, siteLang === 'nb', translated, k); }}
            />
        </div>
    );
};

export default Aktivering;
